import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { SUBBRAND_DETAILS, SUBBRANDS } from "../data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function BrandPage() {
  const { slug } = useParams();
  const brand = SUBBRAND_DETAILS[slug];

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (!brand) return <Navigate to="/" replace />;

  const others = SUBBRANDS.filter((b) => b.slug !== slug);

  return (
    <main data-testid="brand-page" className="bg-[#050505]">
      {/* HERO */}
      <section data-testid="brand-hero" className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={brand.image} alt={brand.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pb-20 pt-40">
          <motion.div initial="hidden" animate="visible">
            <motion.div variants={fadeUp} custom={0}>
              <Link data-testid="brand-back-link" to="/#brands" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-wtei-secondary hover:text-wtei-primary transition-colors mb-8">
                <ArrowLeft strokeWidth={1.5} size={16} /> All Brands
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} custom={1} className="text-xs md:text-sm uppercase tracking-[0.3em] text-wtei-primary font-medium mb-5">
              {brand.tagline}
            </motion.p>
            <motion.h1 variants={fadeUp} custom={2} className="font-heading text-5xl md:text-7xl font-light leading-tight text-wtei-text">
              {brand.name}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* INTRO + CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="lg:col-span-8">
              <p className="font-heading text-2xl md:text-4xl font-light leading-snug text-wtei-text italic">
                {brand.intro}
              </p>
            </motion.div>
            <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} className="lg:col-span-4 lg:pl-8 lg:border-l border-white/10">
              <p className="text-xs uppercase tracking-[0.25em] text-wtei-secondary mb-4">Official Website</p>
              <a
                data-testid="brand-visit-official"
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 bg-wtei-primary text-black px-8 py-4 uppercase tracking-widest text-sm font-medium hover:bg-wtei-primaryHover transition-all duration-300"
              >
                Visit {brand.urlLabel}
                <ArrowUpRight strokeWidth={1.5} size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-wtei-text mb-14">What we recognize</h2>
          <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {brand.features.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                custom={i % 2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                data-testid={`brand-feature-${i}`}
                className="bg-[#050505] p-10 hover:bg-[#111111] transition-colors duration-500"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex items-center justify-center w-9 h-9 border border-wtei-primary/50 text-wtei-primary">
                    <Check strokeWidth={1.5} size={18} />
                  </span>
                  <h3 className="font-heading text-2xl text-wtei-text">{f.title}</h3>
                </div>
                <p className="font-body text-sm md:text-base text-wtei-secondary font-light leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER BRANDS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-wtei-primary mb-8">Explore More</p>
          <div className="grid md:grid-cols-2 gap-6">
            {others.map((b) => (
              <Link
                key={b.id}
                data-testid={`brand-other-${b.id}`}
                to={`/brand/${b.slug}`}
                className="group relative overflow-hidden border border-white/10 hover:border-wtei-primary/60 transition-all duration-500 h-56"
              >
                <img src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
                <div className="relative z-10 h-full flex flex-col justify-center p-10">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-wtei-primary mb-2">{b.tagline}</p>
                  <h3 className="font-heading text-3xl text-wtei-text group-hover:text-wtei-primary transition-colors">{b.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
